// Delete disk flow. Deleting deprovisions the PersistentDisk itself,
// which destroys the disk and all of its data. This is irreversible — if the
// data may be reused, detach instead and keep the disk.
import {
  createPersistentDiskOperation,
  selectDiskAttachmentsByPersistentDiskId,
} from "@app/deploy";
import {
  useDispatch,
  useLoader,
  useLoaderSuccess,
  useSelector,
} from "@app/react";
import { environmentActivityUrl } from "@app/routes";
import type { WebState } from "@app/schema";
import type { DeployPersistentDisk } from "@app/types";
import { Banner, BannerMessages } from "@app/ui/shared/banner";
import { Button } from "@app/ui/shared/button";
import { IconAlertTriangle } from "@app/ui/shared/icons";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeading,
} from "@app/ui/shared/modal";
import { ModalWrapper } from "@app/ui/shared/modal-portal";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

interface DeletePersistentDiskModalProps {
  isOpen: boolean;
  disk: DeployPersistentDisk;
  onClose: () => void;
}

export const DeletePersistentDiskModal = ({
  isOpen,
  disk,
  onClose,
}: DeletePersistentDiskModalProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Mirror the deploy-api safety guard: a disk can't be deleted while it still
  // has active attachments. Detached (deprovisioned) attachments don't block.
  const attachments = useSelector((s: WebState) =>
    selectDiskAttachmentsByPersistentDiskId(s, { persistentDiskId: disk.id }),
  );
  const activeAttachments = useMemo(
    () => attachments.filter((a) => a.status !== "deprovisioned"),
    [attachments],
  );
  const hasActiveAttachments = activeAttachments.length > 0;

  const loader = useLoader(createPersistentDiskOperation);

  useLoaderSuccess(loader, () => {
    // TODO: use appActivityUrl once operations properly populate targetResources
    // with the service and app being detached from.
    navigate(environmentActivityUrl(disk.environmentId));
  });

  const handleConfirm = () => {
    // Backstop: never dispatch while the attach guard is tripped.
    if (hasActiveAttachments) return;
    dispatch(
      createPersistentDiskOperation({ id: disk.id, type: "deprovision" }),
    );
  };

  if (!isOpen) return null;

  return (
    <ModalWrapper>
      <Modal isOpen={isOpen} onClose={onClose} className="min-w-[600px]">
        <ModalHeading
          icon={<IconAlertTriangle variant="sm" className="text-red-500" />}
          title={`Delete Disk: ${disk.handle}`}
          onClose={onClose}
        />
        <ModalBody className="pb-0">
          <Banner variant="error" className="mb-4">
            Deleting <strong>{disk.handle}</strong> destroys the disk and all of
            its data. This cannot be undone. To retain the data for future use,
            detach the disk now and reattach it when needed.
          </Banner>

          {hasActiveAttachments ? (
            <Banner variant="warning" className="mb-4">
              This disk is still attached to{" "}
              {activeAttachments.length === 1
                ? "a service"
                : `${activeAttachments.length} services`}
              . Detach it from all services before deleting.
            </Banner>
          ) : null}

          <BannerMessages className="mt-4" {...loader} />
        </ModalBody>
        <ModalFooter
          actions={[
            <Button key="cancel" variant="white" onClick={onClose}>
              Cancel
            </Button>,
            <Button
              key="confirm"
              variant="delete"
              disabled={hasActiveAttachments}
              onClick={handleConfirm}
              isLoading={loader.isLoading}
            >
              Delete Permanently
            </Button>,
          ]}
        />
      </Modal>
    </ModalWrapper>
  );
};
